import React, { useState, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import { deleteChatV2 } from '../../../api/chat';
import './LeftPanel.css';
import logger from '../../../utils/logger';
import { usePanelState } from './panelState';
import { analytics } from '../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { isCodexUi } from '../../../utils/codexEnv';
import MiniChatItem from './MiniChatItem';
import ChatGroup from './ChatGroup';
import ChatSearch from './ChatSearch';

// Custom sidebar toggle icon matching Claude.ai style
// Simple panel/sidebar icon that remains consistent in both states
const SidebarToggleIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <line x1="7" y1="7" x2="7" y2="17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="9.5" y1="7" x2="9.5" y2="17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const LeftPanel = ({
  isOpen,
  recentChats,
  recentInstruments = [],
  chatEnabled = true,
  onTogglePanel,
  onChatSelect,
  onInstrumentSelect,
  selectedChatId,
  selectedInstrument,
  auth,
  onChatsUpdated,
  instruments,
  searchPlaceholder
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { isExpanded } = usePanelState();
  const [isDeleting, setIsDeleting] = useState(false);

  // Track panel state changes
  useEffect(() => {
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'left_panel_state_changed', {
        is_open: isOpen,
        is_expanded: isExpanded,
        chat_count: recentChats.length,
        selected_chat_id: selectedChatId
      });
    }
  }, [isOpen, isExpanded, recentChats.length, selectedChatId]);


  // Track chat selection
  const handleChatSelect = (chat) => {
    logger.info('Chat selected:', { chat: chat });

    onChatSelect(chat);
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'chat_history_selected', {
        chat_id: chat.chat_id,
        chat_name: chat.chat_name,
        previous_chat_id: selectedChatId,
        chat_age: Date.now() - new Date(chat.created_at).getTime()
      });
    }
  };

  // Track instrument selection (when chat is disabled)
  const handleInstrumentItemSelect = (item) => {
    logger.info('Instrument history selected:', { instrument: item.instrument });
    if (onInstrumentSelect) {
      onInstrumentSelect(item.instrument, item.category);
    }
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'instrument_history_selected', {
        instrument: item.instrument,
        category: item.category
      });
    }
  };

  // Track chat deletion
  const handleDeleteChat = async (chatId, chatName) => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteChatV2(auth, (response) => {
        if (response && !response.error) {
          onChatsUpdated();

          if (!isCodexUi && analytics) {
            logEvent(analytics, 'chat_deleted', {
              chat_id: chatId,
              chat_name: chatName,
              chat_age: Date.now() - new Date(response.created_at).getTime(),
              was_selected: chatId === selectedChatId
            });
          }
        } else {
          logger.error('Failed to delete chat:', response?.error);
        }
      }, chatId);
    } catch (error) {
      logger.error('Error deleting chat:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Track panel expansion/collapse
  const handlePanelToggle = () => {
    onTogglePanel();

    if (!isCodexUi && analytics) {
      logEvent(analytics, 'left_panel_toggled', {
        new_state: !isOpen ? 'expanded' : 'collapsed',
        previous_state: isOpen ? 'expanded' : 'collapsed',
        chat_count: recentChats.length
      });
    }
  };

  // Track chat history updates
  useEffect(() => {
    if (recentChats.length > 0) {
      if (!isCodexUi && analytics) {
        logEvent(analytics, 'chat_history_updated', {
          total_chats: recentChats.length,
          new_chats: recentChats.filter(chat =>
            Date.now() - new Date(chat.created_at).getTime() < 60000
          ).length,
          selected_chat_id: selectedChatId
        });
      }
    }
  }, [recentChats, selectedChatId]);

  // Sort and filter chats
  const sortedChats = [...recentChats]
    .sort((a, b) => b.updated_at - a.updated_at)
    .filter(chat => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      const chatName = (chat.chat_name || '').toLowerCase().replace('_', '/');
      return chatName.includes(query);
    });

  // Filter instruments by search query
  const filteredInstruments = recentInstruments.filter(item => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const label = (item.label || item.instrument || '').toLowerCase();
    return label.includes(query);
  });

  return (
    <div className={`left-panel ${!isOpen ? 'collapsed' : ''}`}>
      <button
        className="panel-toggle-internal"
        onClick={handlePanelToggle}
        aria-label={isOpen ? 'Collapse panel' : 'Expand panel'}
        title={isOpen ? 'Collapse panel (⌘B)' : 'Expand panel (⌘B)'}
      >
        <SidebarToggleIcon />
      </button>

      <div className="recent-chats">
        {!isOpen ? (
          <div className="mini-chats">
            {chatEnabled ? (
              sortedChats.map((chat) => (
                <MiniChatItem
                  key={chat.chat_id}
                  chat={chat}
                  isSelected={selectedChatId === chat.chat_id}
                  onChatSelect={handleChatSelect}
                  instruments={instruments}
                />
              ))
            ) : (
              filteredInstruments.map((item) => (
                <Tooltip key={item.instrument} title={item.label} placement="right" arrow>
                  <div
                    className={`mini-chat-item ${selectedInstrument === item.instrument ? 'selected' : ''}`}
                    onClick={() => handleInstrumentItemSelect(item)}
                  >
                    <span className="mini-chat-letter">{item.label?.[0] || item.instrument?.[0] || '?'}</span>
                  </div>
                </Tooltip>
              ))
            )}
          </div>
        ) : (
          <>
            {chatEnabled && (
              <ChatSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={searchPlaceholder || 'Search chats...'}
              />
            )}
            {chatEnabled ? (
              <ChatGroup
                chats={sortedChats}
                selectedChatId={selectedChatId}
                onChatSelect={handleChatSelect}
                instruments={instruments}
                onDeleteChat={handleDeleteChat}
              />
            ) : (
              <div className="instrument-history-list">
                {filteredInstruments.length === 0 ? (
                  <div className="no-instruments-message">
                    No recent instruments. Select an instrument to start.
                  </div>
                ) : (
                  filteredInstruments.map((item) => (
                    <div
                      key={item.instrument}
                      className={`instrument-history-item ${selectedInstrument === item.instrument ? 'selected' : ''}`}
                      onClick={() => handleInstrumentItemSelect(item)}
                    >
                      <span className="instrument-label">{item.label}</span>
                      <span className="instrument-category">{item.category}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeftPanel; 
